import { supabase } from "@/lib/supabase/client";
import {
  computeOutreachStage,
  defaultFollowUpAtFrom,
} from "@/lib/leads/pinch-hit-outreach";
import { outreachQueryKeys } from "@/lib/outreach/query-keys";
import { invalidateWeekQueueCaches } from "@/lib/outreach/invalidate-week-queue";
import type {
  LeadOutreachEvent,
  LeadOutreachEventInsert,
  LeadOutreachRollup,
  OutreachStage,
} from "@/types/lead-outreach";
import type { QueryClient } from "@tanstack/react-query";
import { assertOutreachActivityCommandsEnabled } from "@/lib/outreach/activity-release";

const OUTREACH_EVENTS_CHUNK_SIZE = 100;
const OUTREACH_EVENTS_MAX_ROWS = 10_000;
const pendingEventCorrelationIds = new Map<string, string>();

function eventOperationKey(data: LeadOutreachEventInsert): string {
  // Exclude generated occurrence time. A lost response retried from the same
  // screen must reuse its correlation ID, while a completed later action gets
  // a new ID after this entry is cleared.
  return JSON.stringify({
    lead_id: data.lead_id,
    track: data.track,
    event_type: data.event_type,
    channel: data.channel ?? 'manual_email',
    template_key: data.template_key?.trim() || null,
    subject: data.subject?.trim() || null,
    proof_url: data.proof_url?.trim() || null,
    body_preview: data.body_preview?.trim() || null,
    follow_up_at: data.follow_up_at ?? null,
    notes: data.notes?.trim() || null,
  });
}

async function listRedactedEvents(
  leadIds?: string[],
  limit = OUTREACH_EVENTS_MAX_ROWS,
): Promise<LeadOutreachEvent[]> {
  const { data, error } = await supabase.rpc(
    "list_legacy_lead_outreach_events",
    {
      p_lead_ids: leadIds?.length ? leadIds : null,
      p_limit: limit,
    },
  );
  if (error)
    throw new Error(`Failed to fetch outreach events: ${error.message}`);
  return (data as LeadOutreachEvent[]) || [];
}

function chunkValues<T>(values: T[], chunkSize: number): T[][] {
  const chunks: T[][] = [];
  for (let index = 0; index < values.length; index += chunkSize) {
    chunks.push(values.slice(index, index + chunkSize));
  }
  return chunks;
}

function requiredText(value: string, fieldName: string): string {
  const trimmed = value.trim();
  if (!trimmed) {
    throw new Error(`${fieldName} is required.`);
  }
  return trimmed;
}

export const leadOutreachService = {
  async listEvents(leadId: string): Promise<LeadOutreachEvent[]> {
    try {
      return await listRedactedEvents([leadId]);
    } catch (error) {
      console.error("Error fetching lead outreach events:", error);
      return [];
    }
  },

  async listAllEvents(): Promise<LeadOutreachEvent[]> {
    try {
      return await listRedactedEvents(undefined, OUTREACH_EVENTS_MAX_ROWS);
    } catch (error) {
      console.error("Error fetching all lead outreach events:", error);
      if (error instanceof Error) throw error;
      throw new Error("Failed to fetch outreach events");
    }
  },

  async listEventsForLeadIds(leadIds: string[]): Promise<LeadOutreachEvent[]> {
    if (leadIds.length === 0) return [];

    const events: LeadOutreachEvent[] = [];
    for (const leadIdChunk of chunkValues(
      leadIds,
      OUTREACH_EVENTS_CHUNK_SIZE,
    )) {
      events.push(...(await listRedactedEvents(leadIdChunk)));
    }

    return events.sort(
      (left, right) =>
        new Date(right.occurred_at).getTime() -
        new Date(left.occurred_at).getTime(),
    );
  },

  async createEvent(data: LeadOutreachEventInsert): Promise<LeadOutreachEvent> {
    assertOutreachActivityCommandsEnabled();
    const operationKey = eventOperationKey(data);
    const correlationId = pendingEventCorrelationIds.get(operationKey) ?? crypto.randomUUID();
    pendingEventCorrelationIds.set(operationKey, correlationId);
    const insertData = {
      lead_id: data.lead_id,
      track: data.track,
      event_type: data.event_type,
      channel: data.channel ?? "manual_email",
      template_key: data.template_key?.trim() || null,
      subject: data.subject?.trim() || null,
      proof_url: data.proof_url?.trim() || null,
      body_preview: data.body_preview?.trim() || null,
      occurred_at: data.occurred_at ?? new Date().toISOString(),
      follow_up_at: data.follow_up_at ?? null,
      notes: data.notes?.trim() || null,
      created_by: data.created_by?.trim() || null,
    };

    const { data: created, error } = await supabase.rpc(
      "append_legacy_lead_outreach_event",
      {
        p_lead_id: insertData.lead_id,
        p_track: insertData.track,
        p_event_type: insertData.event_type,
        p_channel: insertData.channel,
        p_template_key: insertData.template_key,
        p_subject: insertData.subject,
        p_proof_url: insertData.proof_url,
        p_body_preview: insertData.body_preview,
        p_occurred_at: insertData.occurred_at,
        p_follow_up_at: insertData.follow_up_at,
        p_notes: insertData.notes,
        p_correlation_id: correlationId,
      },
    );

    if (error) {
      console.error("Error creating lead outreach event:", error);
      throw new Error(`Failed to log outreach event: ${error.message}`);
    }
    pendingEventCorrelationIds.delete(operationKey);
    return created as LeadOutreachEvent;
  },

  async logSent(data: {
    lead_id: string;
    track: LeadOutreachEventInsert["track"];
    template_key?: string | null;
    subject: string;
    proof_url?: string | null;
    body_preview?: string | null;
    follow_up_days?: number;
    occurred_at?: string;
    notes?: string | null;
  }): Promise<LeadOutreachEvent> {
    const isSecondFollowUp = data.template_key === "follow_up_2";
    const isFirstFollowUp = data.template_key === "follow_up";
    const occurredAt = data.occurred_at ?? new Date().toISOString();
    const followUpAt = isSecondFollowUp
      ? null
      : defaultFollowUpAtFrom(
          occurredAt,
          data.follow_up_days ?? (isFirstFollowUp ? 6 : 4),
        );
    return this.createEvent({
      lead_id: data.lead_id,
      track: data.track,
      event_type:
        isFirstFollowUp || isSecondFollowUp ? "follow_up_sent" : "sent",
      template_key: data.template_key,
      subject: requiredText(data.subject, "Subject"),
      proof_url: data.proof_url,
      body_preview: data.body_preview,
      occurred_at: occurredAt,
      follow_up_at: followUpAt,
      notes: data.notes,
    });
  },

  mergeEventIntoRollupCache(
    queryClient: QueryClient,
    event: LeadOutreachEvent,
  ): void {
    queryClient.setQueriesData<LeadOutreachEvent[]>(
      { queryKey: outreachQueryKeys.rollupsRoot },
      (current) => {
        if (!current) return [event];
        const withoutDuplicate = current.filter((row) => row.id !== event.id);
        return [event, ...withoutDuplicate].sort(
          (left, right) =>
            new Date(right.occurred_at).getTime() -
            new Date(left.occurred_at).getTime(),
        );
      },
    );
  },

  async invalidateOutreachQueries(
    queryClient: QueryClient,
    leadId: string,
    weekStart?: string,
  ): Promise<void> {
    await queryClient.invalidateQueries({
      queryKey: outreachQueryKeys.events(leadId),
    });
    if (weekStart) {
      await invalidateWeekQueueCaches(queryClient, weekStart, leadId);
    } else {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: outreachQueryKeys.rollupsRoot,
          refetchType: "active",
        }),
        queryClient.invalidateQueries({ queryKey: ["leads"] }),
      ]);
    }
  },

  async logQueued(data: {
    lead_id: string;
    track: LeadOutreachEventInsert["track"];
    template_key?: string | null;
    notes?: string | null;
  }): Promise<LeadOutreachEvent> {
    return this.createEvent({
      lead_id: data.lead_id,
      track: data.track,
      event_type: "queued",
      template_key: data.template_key,
      notes: data.notes,
    });
  },

  buildRollups(
    leadIds: string[],
    events: LeadOutreachEvent[],
    queuedLeadIds: Set<string>,
  ): Map<string, LeadOutreachRollup> {
    const eventsByLead = new Map<string, LeadOutreachEvent[]>();
    for (const event of events) {
      const list = eventsByLead.get(event.lead_id) ?? [];
      list.push(event);
      eventsByLead.set(event.lead_id, list);
    }

    const rollups = new Map<string, LeadOutreachRollup>();
    for (const leadId of leadIds) {
      const leadEvents = [...(eventsByLead.get(leadId) ?? [])].sort(
        (left, right) =>
          new Date(right.occurred_at).getTime() -
          new Date(left.occurred_at).getTime(),
      );
      const stage = computeOutreachStage(leadEvents, queuedLeadIds, leadId);
      const latestTouch = leadEvents.find((event) =>
        ["sent", "follow_up_sent", "replied"].includes(event.event_type),
      );
      const latestAny = leadEvents[0];
      const sendCount = leadEvents.filter((event) =>
        ["sent", "follow_up_sent"].includes(event.event_type),
      ).length;
      const nextFollowUp =
        leadEvents
          .map((event) => event.follow_up_at)
          .filter(
            (value): value is string =>
              !!value && new Date(value).getTime() > Date.now(),
          )
          .sort()[0] ?? null;

      rollups.set(leadId, {
        lead_id: leadId,
        stage,
        track: latestAny?.track ?? null,
        latest_template_key:
          latestTouch?.template_key ?? latestAny?.template_key ?? null,
        latest_subject: latestTouch?.subject ?? null,
        last_touch_at: latestTouch?.occurred_at ?? null,
        next_follow_up_at: nextFollowUp,
        send_count: sendCount,
        is_queued_this_week:
          queuedLeadIds.has(leadId) &&
          !["sent", "follow_up_due", "replied", "opted_out"].includes(stage),
      });
    }
    return rollups;
  },

  stageFilterMatches(
    stage: OutreachStage,
    rollup: LeadOutreachRollup | undefined,
  ): boolean {
    if (!rollup) return stage === "not_contacted";
    return rollup.stage === stage;
  },
};
